import { useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import { BOARD_CELL_CARD_CAP, type BoardCard, type BoardCellAggregate } from "@salesboard/contracts";
import { useT } from "@/i18n";
import { cn } from "@/lib/utils";
import { formatCurrencyCompact } from "@/lib/format";
import { OpportunityCard } from "./OpportunityCard";
import { OpportunityChip } from "./OpportunityChip";
import type { BoardDensity } from "./useBoardDensity";

/**
 * One cell of the grid: a stage row crossed with a quarter column, and a drop target.
 *
 * The cell is the *only* kind of drop target that exists, which is what makes snapping
 * structural — a drop can only ever resolve to a stage and a quarter, so there is no pixel
 * arithmetic anywhere and nothing to round.
 *
 * **A capped cell is still a drop target**, and so is a collapsed one. That is legitimate
 * because within-cell order is derived, never manual: dropping on the summary means exactly
 * what dropping on the expanded cell means, because a drop never has to choose a position.
 */
export function BoardCell({
  stageId,
  stageName,
  quarter,
  cards,
  aggregate,
  canDrop,
  density = "normal",
  collapsed = false,
  currentUserId,
  isHeadOfSales,
  pendingCardId,
  onOpenCard,
  onHoverCard,
  onBlockedDrag,
}: {
  stageId: string;
  stageName: string;
  quarter: number;
  cards: BoardCard[];
  aggregate: BoardCellAggregate | undefined;
  canDrop: boolean;
  /** Cards, or the condensed chips. See `useBoardDensity`. */
  density?: BoardDensity;
  /** The row is collapsed: the aggregate strip alone, and still a drop target. */
  collapsed?: boolean;
  currentUserId: string | undefined;
  isHeadOfSales: boolean;
  pendingCardId?: string;
  onOpenCard: (card: BoardCard) => void;
  onHoverCard?: (card: BoardCard) => void;
  onBlockedDrag: (card: BoardCard) => void;
}) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const droppable = useDroppable({ id: `cell:${stageId}:${quarter}`, data: { stageId, quarter }, disabled: !canDrop });

  const condensed = density === "condensed";
  /**
   * Condensed renders **every** chip; cards keep their cap.
   *
   * The cap (M32) bounds a cell of cards, where seven of them plus a "+ N more" is already
   * ~500px of column. A chip is roughly a fifth of a card's height and wraps beside its
   * neighbours, so the same content costs a fraction of the room — and the client, who owned
   * that decision jointly and had not yet ratified it, asked for this density to show all of
   * them (2026-08-27, specs/board/condensed-density.md). Hiding deals behind a control is the
   * opposite of a view whose whole purpose is fitting more of them on one screen.
   *
   * It also makes the cell more truthful, not less: the value above is the server's, and
   * uncapping means the chips a reader can count are the ones that produced it.
   */
  const visible = condensed || expanded ? cards : cards.slice(0, BOARD_CELL_CARD_CAP);
  const movable = (card: BoardCard): boolean => canDrop && (isHeadOfSales || card.ownerId === currentUserId);
  // From the server's count, not from what happens to be rendered — the whole point of
  // aggregating in SQL.
  const hidden = Math.max(0, (aggregate?.count ?? cards.length) - visible.length);

  /**
   * The hovered cell previews *what it will look like after the drop*: the tint is the
   * dragged card's owner colour where they have one, primary where they do not. The active
   * card rides dnd-kit's drag data, so no cell has to be told who is being moved.
   */
  const draggedColor = (droppable.active?.data.current as { card?: BoardCard } | undefined)?.card?.ownerColor;
  const dropColor = draggedColor ? `var(--manager-${draggedColor})` : "var(--primary)";

  return (
    <div
      ref={droppable.setNodeRef}
      role="group"
      // Named by where it is, not just by what it holds: out of visual context, "3
      // opportunities" says nothing about which stage and quarter they are in.
      aria-label={t(
        "board.cell",
        stageName,
        quarter,
        t("count.opportunities", aggregate?.count ?? 0),
        formatCurrencyCompact(aggregate?.totalNip ?? "0"),
      )}
      className={cn(
        "border-border/60 flex flex-col gap-1.5 border-r border-b p-1.5 transition-colors",
        // A condensed cell needs far less floor than a card cell, and asking for 6rem of it
        // would undo most of what the density is for.
        collapsed ? "min-h-8 justify-center" : condensed ? "min-h-10" : "min-h-24",
        // A filled surface reads as "this container will receive it" where a thin ring
        // reads as focus — the fill and the 2px inset ring are one class, see index.css.
        droppable.isOver && "cell-drop-target",
      )}
      style={droppable.isOver ? ({ "--drop-color": dropColor } as React.CSSProperties) : undefined}
    >
      {aggregate && aggregate.count > 0 && (
        // The value alone (2026-08-25): the count is already countable — the cards are right
        // there, and the row header carries the stage's own count. Money is the figure a cell
        // is read for. It stays in the cell's accessible name above, so nothing is lost to a
        // reader who cannot count the cards by eye.
        <div className="text-muted-foreground board-type-meta flex items-baseline justify-end px-0.5">
          <span className="tabular font-medium">{formatCurrencyCompact(aggregate.totalNip)}</span>
        </div>
      )}

      {/* Collapsed keeps the aggregates and drops the cards: hiding the figures would make
          collapsing cost information, and then nobody would collapse anything. */}
      {!collapsed && (
        <>
          {condensed ? (
            /* `flex-wrap`: chips sit next to each other while there is room and wrap when there
               is not, which is the packing the density exists for. */
            <div className="flex flex-wrap content-start gap-1">
              {visible.map((card) => (
                <OpportunityChip
                  key={card.id}
                  card={card}
                  movable={movable(card)}
                  pending={pendingCardId === card.id}
                  onOpen={onOpenCard}
                  onHover={onHoverCard}
                  onBlockedDrag={onBlockedDrag}
                />
              ))}
            </div>
          ) : (
            visible.map((card) => (
              <OpportunityCard
                key={card.id}
                card={card}
                movable={movable(card)}
                pending={pendingCardId === card.id}
                onOpen={onOpenCard}
                onHover={onHoverCard}
                onBlockedDrag={onBlockedDrag}
              />
            ))
          )}

          {!condensed && hidden > 0 && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="text-muted-foreground hover:text-foreground hover:bg-accent board-type-secondary rounded px-1 py-1 text-left"
            >
              {t("count.moreCards", hidden)}
              <span className="sr-only">{t("board.moreCardsIn", stageName, quarter)}</span>
            </button>
          )}

          {!condensed && expanded && cards.length > BOARD_CELL_CARD_CAP && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="text-muted-foreground hover:text-foreground board-type-secondary rounded px-1 text-left"
            >
              {t("common.showLess")}
            </button>
          )}
        </>
      )}
    </div>
  );
}
